import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Compass,
  Palette,
  Wrench,
  ShieldCheck,
  BarChart3,
  Sparkles,
} from "lucide-react";
import ApartmentCard from "../components/ApartmentCard";
import { fetchApartments } from "../lib/api";

const pillars = [
  {
    n: "01",
    tag: "Positioning",
    icon: Compass,
    title: "Portfolio Positioning",
    body: "Yield modelling, guest positioning and licensing for owners looking to reposition a residential asset into a premium serviced apartment.",
  },
  {
    n: "02",
    tag: "Interiors",
    icon: Palette,
    title: "Interior Curation",
    body: "Hand-styled interiors, textiles and photography — the editorial standard that separates the top decile from the middle of the market.",
  },
  {
    n: "03",
    tag: "Operations",
    icon: Wrench,
    title: "Managed Operations",
    body: "A turnkey operating layer: concierge, PMS, housekeeping, maintenance and monthly reporting — for owners who prefer passive returns.",
  },
];

export default function Home() {
  const [apartments, setApartments] = useState([]);

  useEffect(() => {
    fetchApartments().then(setApartments).catch((e) => console.error(e));
  }, []);

  const hero = apartments[0]?.images?.[0] || "/apartments/generated/the-exchequer-01.jpg";

  return (
    <div data-testid="home-page">
      {/* HERO — owner-facing consulting */}
      <section className="relative isolate overflow-hidden">
        <div className="container-luxe grid items-center gap-12 pb-20 pt-10 lg:grid-cols-12 lg:pb-32 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <span className="overline" data-testid="hero-overline">
              Boutique Property Management &amp; Hospitality Consultancy · UK
            </span>
            <h1 className="mt-5 font-[Cormorant_Garamond] text-5xl leading-[1.05] tracking-tight text-[#1A1D1A] md:text-6xl lg:text-7xl">
              A quieter, more <em className="not-italic text-[#7A8C7B]">considered way</em> to own a serviced apartment.
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-[#4A524C] md:text-lg">
              Sagita Rasmadi LLC is a boutique consultancy that manages
              premium serviced apartments across the United Kingdom on behalf
              of private owners, family offices and investors — combining
              design, operations and yield strategy into a single, calm mandate.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="btn-primary"
              >
                Request Consultation
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="btn-secondary"
              >
                Our Services
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.2em] text-[#4A524C]">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#C6A66B]" />
                Owner-Aligned Mandates
              </span>
              <span className="hidden h-3 w-px bg-[#C6A66B]/40 sm:inline-block" />
              <span className="inline-flex items-center gap-2">
                <BarChart3 size={14} className="text-[#C6A66B]" />
                Yield-Focused Strategy
              </span>
              <span className="hidden h-3 w-px bg-[#C6A66B]/40 sm:inline-block" />
              <span className="inline-flex items-center gap-2">
                <Sparkles size={14} className="text-[#C6A66B]" />
                Editorial Standard
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative lg:col-span-6"
          >
            <div className="relative overflow-hidden rounded-[28px] shadow-[0_30px_60px_rgba(26,29,26,0.12)]">
              <div className="aspect-[4/5] w-full bg-[#D9CFC2]">
                <img
                  src={hero}
                  alt="A residence under Sagita management"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1D1A]/40 via-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 text-white">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.28em] opacity-80">
                    Managed on behalf of the owner
                  </span>
                  <p className="mt-1 font-[Cormorant_Garamond] text-3xl">
                    {apartments[0]?.name ?? "The Exchequer"}
                  </p>
                </div>
                <Link
                  to={`/portfolio/${apartments[0]?.id ?? "the-exchequer"}`}
                  data-testid="hero-featured-cta"
                  className="rounded-full bg-white/95 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[#1A1D1A] transition hover:bg-[#C6A66B] hover:text-white"
                >
                  Case Study
                </Link>
              </div>
            </div>

            <div className="absolute -bottom-8 -left-6 hidden w-60 rounded-2xl border border-[#D9CFC2]/60 bg-white p-5 shadow-[0_20px_40px_rgba(26,29,26,0.08)] md:block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C6A66B]">
                Practice snapshot
              </div>
              <p className="mt-2 font-[Cormorant_Garamond] text-2xl text-[#1A1D1A]">
                +38% avg. yield uplift
              </p>
              <p className="mt-1 text-xs leading-relaxed text-[#4A524C]">
                across repositioned units in our managed portfolio.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-luxe grid gap-16 lg:grid-cols-12 lg:gap-24">
          <div className="lg:col-span-5">
            <span className="overline">Who We Serve</span>
            <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl leading-tight text-[#1A1D1A] md:text-5xl">
              We do not rent apartments. We manage them for the people who own them.
            </h2>
            <div className="mt-6 h-px w-16 bg-[#C6A66B]" />
          </div>
          <div className="space-y-5 text-base leading-relaxed text-[#4A524C] lg:col-span-7 lg:text-lg">
            <p>
              Sagita Rasmadi LLC is a boutique consulting practice. Every
              residence you see on this site is owned by a private client — we
              simply operate it on their behalf under a transparent, written
              mandate.
            </p>
            <p>
              Our clients are private owners of London property, family offices
              with cross-border portfolios, first-time investors seeking a
              fully outsourced set-up, and boutique operators who need a
              seasoned partner to lift the standard of their existing units.
            </p>
            <p>
              We do not aggregate inventory or sell stays. Owners retain full
              title and control; we take care of design, operations, guests and
              reporting — quietly and consistently.
            </p>
            <Link to="/about" data-testid="home-about-link" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#7A8C7B] hover:text-[#1A1D1A]">
              Read our practice principles
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT WE DO — Three pillars */}
      <section className="py-24 md:py-28">
        <div className="container-luxe">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <span className="overline">What We Do</span>
            <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl text-[#1A1D1A] md:text-5xl">
              Three services. One mandate.
            </h2>
            <div className="gold-divider mt-6" />
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="group flex h-full flex-col rounded-2xl border border-[#D9CFC2]/60 bg-white p-8 shadow-[0_8px_30px_rgba(26,29,26,0.04)] transition-all hover:shadow-[0_24px_50px_rgba(26,29,26,0.08)]"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-[Cormorant_Garamond] text-2xl italic text-[#C6A66B]">
                      {p.n}
                    </span>
                    <span className="overline">{p.tag}</span>
                  </div>
                  <div className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#C6A66B]/60 text-[#7A8C7B]">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-6 font-[Cormorant_Garamond] text-3xl text-[#1A1D1A]">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#4A524C]">
                    {p.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="home-services-cta"
              className="btn-secondary"
            >
              Explore the Practice
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SELECTED CASE STUDIES */}
      <section className="bg-[#D9CFC2]/20 py-24 md:py-32" id="portfolio">
        <div className="container-luxe">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <span className="overline">Selected Case Studies</span>
            <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl text-[#1A1D1A] md:text-5xl">
              A discreet portfolio of managed residences
            </h2>
            <div className="gold-divider mt-6" />
            <p className="mt-6 text-base text-[#4A524C]">
              Every property below is privately owned. Sagita Rasmadi manages
              each one under a bespoke consulting mandate on behalf of its owner.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {apartments.slice(0, 3).map((apt, i) => (
              <ApartmentCard key={apt.id} apartment={apt} index={i} />
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/portfolio"
              data-testid="home-view-all-portfolio"
              className="btn-secondary"
            >
              View Full Portfolio
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* KPI RIBBON */}
      <section className="py-24">
        <div className="container-luxe">
          <div className="grid gap-y-8 rounded-3xl border border-[#D9CFC2]/60 bg-white px-8 py-10 shadow-[0_8px_30px_rgba(26,29,26,0.04)] md:grid-cols-4">
            {[
              { k: "£1M – £15M", l: "Typical asset value" },
              { k: "+38%", l: "Avg. yield uplift" },
              { k: "94%", l: "Median occupancy" },
              { k: "100%", l: "Owner retention (5 yrs)" },
            ].map((s, i) => (
              <div
                key={s.l}
                className={`text-center md:text-left ${
                  i !== 3 ? "md:border-r md:border-[#D9CFC2]/60 md:pr-8" : ""
                }`}
              >
                <div className="font-[Cormorant_Garamond] text-3xl text-[#1A1D1A] md:text-4xl">
                  {s.k}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#7A8C7B]">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="pb-24">
        <div className="container-luxe">
          <div className="relative overflow-hidden rounded-3xl bg-[#7A8C7B] px-8 py-16 text-white md:px-16 md:py-20">
            <div className="grid items-center gap-8 md:grid-cols-12">
              <div className="md:col-span-8">
                <span className="text-[10px] uppercase tracking-[0.28em] text-white/80">
                  Own a property in the UK?
                </span>
                <h3 className="mt-3 font-[Cormorant_Garamond] text-3xl md:text-4xl">
                  Begin a confidential conversation with our practice lead.
                </h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85">
                  Discovery calls are complimentary and there is no obligation.
                  We only take on mandates we can genuinely add value to.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link
                  to="/contact"
                  data-testid="home-cta-strip"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-[#1A1D1A] transition hover:bg-[#C6A66B] hover:text-white"
                >
                  Request Consultation
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
